import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  investigationsService,
  type CreateFromIncidentData,
  type Investigation,
} from '@/services/investigations.service';
import type { Incident } from '@/services/incidents.service';
import { DateInput } from '@/components/ui/DateInput';
import { richTextToPlainText } from '@/utils/richText';
import SubjectPicker from './SubjectPicker';
import { subjectsService, type Subject } from '@/services/subjects.service';

interface Props {
  open: boolean;
  incident: Incident;
  onClose: () => void;
  onCreated: (investigation: Investigation) => void;
}

type FormShape = {
  title: string;
  narrative: string;
  category: string;
  urgency: 'STANDARD' | 'PRIORITY' | 'URGENT';
  subjectName: string;
  subjectType: '' | 'PERSON' | 'ENTITY';
  subjectAddress: string;
  subjectDob: string;
  subjectIdentifiers: string;
  requesterName: string;
  requesterEmail: string;
  requesterPhone: string;
  requesterOrganization: string;
};

function severityToUrgency(severity?: string): 'STANDARD' | 'PRIORITY' | 'URGENT' {
  switch ((severity ?? '').toUpperCase()) {
    case 'LOW':
    case 'MINIMAL':
      return 'STANDARD';
    case 'MEDIUM':
    case 'MODERATE':
      return 'PRIORITY';
    case 'HIGH':
    case 'CRITICAL':
      return 'URGENT';
    default:
      return 'PRIORITY';
  }
}

export default function CreateInvestigationFromIncidentModal({
  open,
  incident,
  onClose,
  onCreated,
}: Props) {
  const initial = useMemo<FormShape>(() => {
    return {
      title: incident.title ?? '',
      narrative: richTextToPlainText(incident.description ?? ''),
      category: incident.category ?? 'OTHER',
      urgency: severityToUrgency(incident.severity),
      subjectName: '',
      subjectType: '',
      subjectAddress: '',
      subjectDob: '',
      subjectIdentifiers: '',
      requesterName: incident.reporterName ?? '',
      requesterEmail: incident.reporterEmail ?? '',
      requesterPhone: incident.reporterPhone ?? '',
      requesterOrganization: incident.reporterOrganization ?? '',
    };
  }, [incident]);

  const [form, setForm] = useState<FormShape>(initial);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recent, setRecent] = useState<Array<{ id: string; caseNumber: string; title: string }>>([]);

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  // Recent investigations preview by subject — best-effort.
  useEffect(() => {
    let alive = true;
    if (!open || !form.subjectName) {
      setRecent([]);
      return () => {
        alive = false;
      };
    }
    investigationsService
      .getInvestigations({ search: form.subjectName, limit: 5 })
      .then((res) => {
        if (!alive) {
          return;
        }
        const items = (res?.investigations ?? []) as Array<{
          id: string;
          caseNumber: string;
          title: string;
        }>;
        setRecent(items);
      })
      .catch(() => {
        /* preview is best-effort */
      });
    return () => {
      alive = false;
    };
  }, [open, form.subjectName]);

  const editedFields = useMemo(() => {
    const out: string[] = [];
    (Object.keys(form) as Array<keyof FormShape>).forEach((k) => {
      if (form[k] !== initial[k]) {
        out.push(k);
      }
    });
    return out;
  }, [form, initial]);

  if (!open) {
    return null;
  }

  const totalFields = Object.keys(form).length;
  const editedCount = editedFields.length;

  const setField = (key: keyof FormShape, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const inputBorder = (key: keyof FormShape) =>
    editedFields.includes(key)
      ? 'border-secondary-300 dark:border-secondary-700'
      : 'border-l-2 border-l-blue-300/60';

  const onSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const effectiveSubjectName = selectedSubject
        ? `${selectedSubject.firstName} ${selectedSubject.lastName}`.trim()
        : form.subjectName;
      const effectiveSubjectType: FormShape['subjectType'] = selectedSubject ? 'PERSON' : form.subjectType;
      const effectiveSubjectAddress = selectedSubject
        ? [
            selectedSubject.addressLine1,
            selectedSubject.city,
            selectedSubject.state,
            selectedSubject.postalCode,
          ]
            .filter(Boolean)
            .join(', ')
        : form.subjectAddress;

      const payload: CreateFromIncidentData = {
        sourceIncidentId: incident.id,
        title: form.title.trim(),
        narrative: form.narrative.trim(),
        category: form.category,
        urgency: form.urgency,
        subjectName: effectiveSubjectName || undefined,
        subjectType: effectiveSubjectType || undefined,
        subjectAddress: effectiveSubjectAddress || undefined,
        subjectDob: form.subjectDob || undefined,
        subjectIdentifiers: form.subjectIdentifiers || undefined,
        requesterName: form.requesterName.trim(),
        requesterEmail: form.requesterEmail.trim(),
        requesterPhone: form.requesterPhone || undefined,
        requesterOrganization: form.requesterOrganization || undefined,
        editedFields,
      };
      const created = await investigationsService.createFromIncident(payload);

      if (selectedSubject) {
        try {
          await subjectsService.linkSubjectToCase(selectedSubject.id, {
            caseId: created.id,
            caseType: 'INVESTIGATION',
          });
        } catch (linkErr: unknown) {
          const msg = linkErr instanceof Error ? linkErr.message : 'Failed to link subject';
          toast.error(`Investigation ${created.caseNumber} created, but linking the subject failed: ${msg}`);
        }
      }

      toast.success(`Investigation ${created.caseNumber} created`);
      onCreated(created);
    } catch (err: unknown) {
      const code = err instanceof Error ? (err as Error & { code?: string }).code : undefined;
      const fallbackMessage = err instanceof Error ? err.message : 'Failed to create investigation';
      const message =
        code === 'INCIDENT_CLOSED'
          ? 'This incident is no longer open. Reload the page and try again.'
          : fallbackMessage;
      setSubmitError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-16 pb-4 overflow-y-auto"
    >
      <div className="bg-white dark:bg-secondary-900 w-full max-w-3xl max-h-[90vh] rounded-lg shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-200 dark:border-secondary-700">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
              Create Investigation
            </h2>
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              Source: {incident.incidentNumber} — {editedCount} of {totalFields} fields edited
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="p-1 rounded hover:bg-secondary-100 dark:hover:bg-secondary-800"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Recent investigations preview */}
          {recent.length > 0 && (
            <div className="rounded border border-secondary-200 dark:border-secondary-700 p-3">
              <p className="text-xs font-semibold text-secondary-700 dark:text-secondary-200 mb-2">
                Recent investigations matching the subject
              </p>
              <ul className="space-y-1">
                {recent.map((r) => (
                  <li key={r.id}>
                    <a
                      href={`/security/investigations/${r.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-primary-600 hover:underline"
                    >
                      {r.caseNumber} — {r.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Title */}
          <div>
            <div className="flex items-center gap-1">
              <label
                htmlFor="vis23-title"
                className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
              >
                Title
              </label>
              {!editedFields.includes('title') && (
                <span className="text-[10px] uppercase tracking-wide text-blue-600">
                  auto-filled
                </span>
              )}
            </div>
            <input
              id="vis23-title"
              className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('title')}`}
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
            />
          </div>

          {/* Narrative */}
          <div>
            <div className="flex items-center gap-1">
              <label
                htmlFor="vis23-narrative"
                className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
              >
                Narrative
              </label>
              {!editedFields.includes('narrative') && (
                <span className="text-[10px] uppercase tracking-wide text-blue-600">
                  auto-filled
                </span>
              )}
            </div>
            <textarea
              id="vis23-narrative"
              rows={6}
              className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('narrative')}`}
              value={form.narrative}
              onChange={(e) => setField('narrative', e.target.value)}
            />
          </div>

          {/* Category + Urgency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="vis23-category"
                  className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
                >
                  Category
                </label>
                {!editedFields.includes('category') && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-600">
                    auto-filled
                  </span>
                )}
              </div>
              <input
                id="vis23-category"
                className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('category')}`}
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="vis23-urgency"
                  className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
                >
                  Urgency
                </label>
                {!editedFields.includes('urgency') && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-600">
                    auto-filled
                  </span>
                )}
              </div>
              <select
                id="vis23-urgency"
                className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('urgency')}`}
                value={form.urgency}
                onChange={(e) => setField('urgency', e.target.value)}
              >
                <option value="STANDARD">Standard</option>
                <option value="PRIORITY">Priority</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
          </div>

          {/* Requester fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="vis23-requesterName"
                  className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
                >
                  Requester Name
                </label>
                {!editedFields.includes('requesterName') && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-600">
                    auto-filled
                  </span>
                )}
              </div>
              <input
                id="vis23-requesterName"
                className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('requesterName')}`}
                value={form.requesterName}
                onChange={(e) => setField('requesterName', e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="vis23-requesterEmail"
                  className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
                >
                  Requester Email
                </label>
                {!editedFields.includes('requesterEmail') && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-600">
                    auto-filled
                  </span>
                )}
              </div>
              <input
                id="vis23-requesterEmail"
                type="email"
                className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('requesterEmail')}`}
                value={form.requesterEmail}
                onChange={(e) => setField('requesterEmail', e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="vis23-requesterPhone"
                  className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
                >
                  Requester Phone
                </label>
                {!editedFields.includes('requesterPhone') && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-600">
                    auto-filled
                  </span>
                )}
              </div>
              <input
                id="vis23-requesterPhone"
                className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('requesterPhone')}`}
                value={form.requesterPhone}
                onChange={(e) => setField('requesterPhone', e.target.value)}
              />
            </div>
            <div>
              <div className="flex items-center gap-1">
                <label
                  htmlFor="vis23-requesterOrganization"
                  className="text-sm font-medium text-secondary-700 dark:text-secondary-200"
                >
                  Requester Organization
                </label>
                {!editedFields.includes('requesterOrganization') && (
                  <span className="text-[10px] uppercase tracking-wide text-blue-600">
                    auto-filled
                  </span>
                )}
              </div>
              <input
                id="vis23-requesterOrganization"
                className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('requesterOrganization')}`}
                value={form.requesterOrganization}
                onChange={(e) => setField('requesterOrganization', e.target.value)}
              />
            </div>
          </div>

          {/* Subject — collapsed by default */}
          <details>
            <summary className="text-sm font-medium cursor-pointer text-secondary-700 dark:text-secondary-200">
              Subject (optional)
            </summary>
            <div className="mt-2 space-y-3">
              <SubjectPicker value={selectedSubject} onChange={setSelectedSubject} />
              {!selectedSubject && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="vis23-subjectName"
                      className="flex items-center gap-1 text-sm font-medium text-secondary-700 dark:text-secondary-200"
                    >
                      Name (freeform)
                    </label>
                    <input
                      id="vis23-subjectName"
                      className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('subjectName')}`}
                      value={form.subjectName}
                      onChange={(e) => setField('subjectName', e.target.value)}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="vis23-subjectType"
                      className="flex items-center gap-1 text-sm font-medium text-secondary-700 dark:text-secondary-200"
                    >
                      Type
                    </label>
                    <select
                      id="vis23-subjectType"
                      className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('subjectType')}`}
                      value={form.subjectType}
                      onChange={(e) =>
                        setField('subjectType', e.target.value)
                      }
                    >
                      <option value="">—</option>
                      <option value="PERSON">Person</option>
                      <option value="ENTITY">Entity</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="vis23-subjectAddress"
                      className="flex items-center gap-1 text-sm font-medium text-secondary-700 dark:text-secondary-200"
                    >
                      Address
                    </label>
                    <input
                      id="vis23-subjectAddress"
                      className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('subjectAddress')}`}
                      value={form.subjectAddress}
                      onChange={(e) => setField('subjectAddress', e.target.value)}
                    />
                  </div>
                  <div>
                    <DateInput
                      id="vis23-subjectDob"
                      label="Date of Birth"
                      className="mt-1"
                      value={form.subjectDob}
                      onChange={(value) => setField('subjectDob', value)}
                    />
                  </div>
                  <div className="col-span-2">
                    <label
                      htmlFor="vis23-subjectIdentifiers"
                      className="flex items-center gap-1 text-sm font-medium text-secondary-700 dark:text-secondary-200"
                    >
                      Identifiers
                    </label>
                    <input
                      id="vis23-subjectIdentifiers"
                      className={`mt-1 w-full rounded border p-2 bg-white dark:bg-secondary-800 ${inputBorder('subjectIdentifiers')}`}
                      value={form.subjectIdentifiers}
                      onChange={(e) => setField('subjectIdentifiers', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          </details>

          {/* Submit error */}
          {submitError && (
            <p className="text-sm text-danger-600 bg-danger-50 dark:bg-danger-900/20 rounded p-2">
              {submitError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-secondary-200 dark:border-secondary-700">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded border border-secondary-300 dark:border-secondary-700 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submitting}
            className="px-4 py-2 rounded bg-primary-600 text-white text-sm disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create Investigation'}
          </button>
        </div>
      </div>
    </div>
  );
}
